import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { cabinetContractApi, cabinetPersonApi } from '../../services/api';
import { ContractD, MyContractRole, PersonD } from '../../types';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import MobileEntityCard from '../../components/MobileEntityCard';
import { useAuth } from '../../contexts/AuthContext';

const ContractsPage: React.FC = () => {
  const { user } = useAuth();
  const isStartTariff = user?.tariff === 'START';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [items, setItems] = useState<ContractD[]>([]);
  const [persons, setPersons] = useState<PersonD[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [counterpartyPersonDId, setCounterpartyPersonDId] = useState<number | ''>('');
  const [myRole, setMyRole] = useState<MyContractRole>('CONTRACTOR');
  const [serial, setSerial] = useState('');
  const [contractDate, setContractDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [amount, setAmount] = useState('');

  const clientInn = user?.inn?.trim();
  const counterpartyOptions = isStartTariff && clientInn
    ? persons.filter((p) => !(p.inn && p.inn.trim() === clientInn))
    : persons;

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [contracts, people] = await Promise.all([
        cabinetContractApi.list(),
        cabinetPersonApi.list().catch(() => [] as PersonD[]),
      ]);
      setItems(contracts);
      setPersons(people);
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!counterpartyPersonDId) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await cabinetContractApi.create({
        counterpartyPersonDId,
        myRole,
        serial,
        contractDate,
        amount: amount ? Number(amount) : null,
        syncToOrd: true,
      });
      setSuccess('Договор создан и отправлен в ОРД');
      setSerial('');
      setAmount('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка создания');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Договоры"
        description={
          isStartTariff
            ? 'Укажите контрагента и свою роль (заказчик или исполнитель). В ОРД уйдёт договор оказания услуг между вами и контрагентом; посредничество с Тренди создаётся автоматически.'
            : 'Укажите свою роль: исполнитель (чаще) или заказчик. Другая сторона — выбранный контрагент.'
        }
      />
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert>}

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }}>
          <FormControl required sx={{ minWidth: 220 }} fullWidth>
            <InputLabel>Контрагент *</InputLabel>
            <Select
              label="Контрагент *"
              value={counterpartyPersonDId}
              onChange={(e) => setCounterpartyPersonDId(e.target.value as number)}
            >
              {counterpartyOptions.map((p) => (
                <MenuItem key={p.personDId} value={p.personDId}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl required sx={{ minWidth: 180 }} fullWidth={isMobile}>
            <InputLabel>Я в договоре *</InputLabel>
            <Select label="Я в договоре *" value={myRole} onChange={(e) => setMyRole(e.target.value as MyContractRole)}>
              <MenuItem value="CONTRACTOR">Исполнитель</MenuItem>
              <MenuItem value="CLIENT">Заказчик</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Номер" value={serial} onChange={(e) => setSerial(e.target.value)} fullWidth={isMobile} />
          <TextField
            required
            label="Дата *"
            type="date"
            value={contractDate}
            onChange={(e) => setContractDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth={isMobile}
          />
          <TextField label="Сумма" value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth={isMobile} />
          <Button
            variant="contained"
            disabled={saving || !counterpartyPersonDId || !contractDate}
            onClick={create}
            sx={{ alignSelf: { xs: 'stretch', md: 'center' }, whiteSpace: 'nowrap' }}
          >
            {saving ? <CircularProgress size={20} /> : 'Создать'}
          </Button>
        </Stack>
      </Paper>

      {loading ? <CircularProgress /> : isMobile ? (
        <Stack spacing={1.25}>
          {items.map((c) => (
            <MobileEntityCard
              key={c.contractDId}
              title={c.counterpartyName || `Договор #${c.contractDId}`}
              subtitle={`#${c.contractDId} · ${c.contractDate || '—'}`}
              chips={
                <>
                  <Chip size="small" label={c.myRole === 'CONTRACTOR' ? 'исполнитель' : 'заказчик'} />
                  <Chip size="small" color={c.syncedToOrd ? 'success' : 'default'} label={c.syncedToOrd ? 'в ОРД' : 'локально'} />
                </>
              }
              meta={[
                { label: 'Номер', value: c.serial || '—' },
                { label: 'Сумма', value: c.amount ?? '—' },
                { label: 'external_id', value: c.externalId || '—' },
              ]}
            />
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Контрагент</TableCell>
                <TableCell>Моя роль</TableCell>
                <TableCell>Номер</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell>Сумма</TableCell>
                <TableCell>external_id</TableCell>
                <TableCell>ОРД</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.contractDId}>
                  <TableCell>{c.contractDId}</TableCell>
                  <TableCell>{c.counterpartyName}</TableCell>
                  <TableCell>{c.myRole === 'CONTRACTOR' ? 'исполнитель' : 'заказчик'}</TableCell>
                  <TableCell>{c.serial}</TableCell>
                  <TableCell>{c.contractDate}</TableCell>
                  <TableCell>{c.amount ?? '—'}</TableCell>
                  <TableCell><code>{c.externalId}</code></TableCell>
                  <TableCell>{c.syncedToOrd ? 'да' : 'нет'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ContractsPage;
